// FreightProof PWA — Zero Trust Freight Compliance
// Roles: carrier-admin, driver, mechanic, broker (read-only)
// Fork of Cook's Garage architecture — same patterns, freight-specific templates

import { initDB } from './stores/db.js';
import { renderFleet } from './pages/fleet.js';
import { renderInspect } from './pages/inspect.js';
import { renderDriver } from './pages/driver.js';
import { renderTrips } from './pages/trips.js';
import { renderSettings } from './pages/settings.js';

const APP_VERSION = '1.0.0';
const API_BASE = 'https://freight.rootz.global/api';

// ─── STATE ──────────────────────────────────────────────────────────

let currentTab = 'fleet';
let role = localStorage.getItem('fp_role') || null;
let carrierDOT = localStorage.getItem('fp_carrier_dot') || null;
let userName = localStorage.getItem('fp_user_name') || null;

// ─── INIT ───────────────────────────────────────────────────────────

async function init() {
  await initDB();

  const app = document.getElementById('app');

  if (!role) {
    renderRoleSelect(app);
    return;
  }

  renderApp(app);
}

// ─── ROLE SELECTION ─────────────────────────────────────────────────

function renderRoleSelect(container) {
  container.innerHTML = `
    <div style="padding: 40px 20px; max-width: 500px; margin: 0 auto; text-align: center;">
      <div style="font-size: 2em; font-weight: 800; color: #4fc3f7; margin-bottom: 8px;">FREIGHTPROOF</div>
      <p style="color: #888; margin-bottom: 32px;">Zero Trust Freight Compliance</p>

      <div class="card" style="text-align: left; margin-bottom: 16px; cursor: pointer;" onclick="window._selectRole('owner-operator')">
        <div class="card-title">Owner Operator</div>
        <div class="card-meta">You're the driver, mechanic, and fleet manager. Register your truck, enroll yourself, run inspections, create trip wallets — all in one place.</div>
      </div>

      <div class="card" style="text-align: left; margin-bottom: 16px; cursor: pointer;" onclick="window._selectRole('carrier')">
        <div class="card-title">Carrier / Fleet Owner</div>
        <div class="card-meta">Manage trucks, trailers, drivers. Run inspections. Create trip wallets. View compliance dashboard.</div>
      </div>

      <div class="card" style="text-align: left; margin-bottom: 16px; cursor: pointer;" onclick="window._selectRole('driver')">
        <div class="card-title">Driver</div>
        <div class="card-meta">Pre-trip inspections, CDL/medical verification, trip logging, manifest capture.</div>
      </div>

      <div class="card" style="text-align: left; margin-bottom: 16px; cursor: pointer;" onclick="window._selectRole('mechanic')">
        <div class="card-title">Mechanic / Inspector</div>
        <div class="card-meta">Annual inspections, maintenance records, repair documentation.</div>
      </div>

      <div class="card" style="text-align: left; cursor: pointer;" onclick="window._selectRole('broker')">
        <div class="card-title">Broker (Read-Only)</div>
        <div class="card-meta">View carrier compliance, vetting records, trip wallet links.</div>
      </div>

      <p style="color: #444; margin-top: 24px; font-size: 0.8em;">v${APP_VERSION} &middot; freight.rootz.global</p>
    </div>
  `;
}

window._selectRole = function(r) {
  role = r;
  localStorage.setItem('fp_role', r);

  // Ask for carrier DOT and name
  const app = document.getElementById('app');
  renderCarrierSetup(app);
};

function renderCarrierSetup(container) {
  container.innerHTML = `
    <div style="padding: 40px 20px; max-width: 500px; margin: 0 auto;">
      <div style="font-size: 1.5em; font-weight: 700; color: #fff; margin-bottom: 20px;">Setup</div>

      <div class="input-group">
        <label>Your Name</label>
        <input type="text" id="setup-name" placeholder="e.g. Andrew Tucker" value="${userName || ''}">
      </div>

      <div class="input-group">
        <label>Carrier DOT Number (if applicable)</label>
        <input type="text" id="setup-dot" placeholder="e.g. 1674" value="${carrierDOT || ''}">
      </div>

      <button class="btn btn-primary" onclick="window._completeSetup()" style="margin-top: 16px;">Get Started</button>
    </div>
  `;
}

window._completeSetup = function() {
  userName = document.getElementById('setup-name').value.trim() || 'User';
  carrierDOT = document.getElementById('setup-dot').value.trim() || '';
  localStorage.setItem('fp_user_name', userName);
  localStorage.setItem('fp_carrier_dot', carrierDOT);
  renderApp(document.getElementById('app'));
};

// ─── MAIN APP SHELL ─────────────────────────────────────────────────

function renderApp(container) {
  const tabs = getTabs();

  container.innerHTML = `
    <nav class="nav">
      <span class="nav-brand">FREIGHTPROOF</span>
      <span>
        <span class="nav-role">${roleLabel(role)}</span>
        <span class="nav-sync ${navigator.onLine ? '' : 'offline'}" id="sync-dot" title="${navigator.onLine ? 'Online' : 'Offline'}"></span>
      </span>
    </nav>
    <main id="page-content"></main>
    <div class="tab-bar">
      ${tabs.map(t => `
        <button class="${t.id === currentTab ? 'active' : ''}" onclick="window._switchTab('${t.id}')">
          <span class="icon">${t.icon}</span>
          ${t.label}
        </button>
      `).join('')}
    </div>
  `;

  // Online/offline indicator
  window.addEventListener('online', () => {
    document.getElementById('sync-dot')?.classList.remove('offline');
  });
  window.addEventListener('offline', () => {
    document.getElementById('sync-dot')?.classList.add('offline');
  });

  renderCurrentTab();
}

function getTabs() {
  switch (role) {
    case 'owner-operator':
      return [
        { id: 'fleet', icon: '🚛', label: 'My Fleet' },
        { id: 'inspect', icon: '📋', label: 'Inspect' },
        { id: 'drivers', icon: '👤', label: 'My ID' },
        { id: 'trips', icon: '📦', label: 'Trips' },
        { id: 'settings', icon: '⚙️', label: 'Settings' },
      ];
    case 'carrier':
      return [
        { id: 'fleet', icon: '🚛', label: 'Fleet' },
        { id: 'inspect', icon: '📋', label: 'Inspect' },
        { id: 'drivers', icon: '👤', label: 'Drivers' },
        { id: 'trips', icon: '📦', label: 'Trips' },
        { id: 'settings', icon: '⚙️', label: 'Settings' },
      ];
    case 'driver':
      return [
        { id: 'fleet', icon: '🚛', label: 'My Truck' },
        { id: 'inspect', icon: '📋', label: 'Inspect' },
        { id: 'drivers', icon: '👤', label: 'My ID' },
        { id: 'trips', icon: '📦', label: 'Trips' },
        { id: 'settings', icon: '⚙️', label: 'Settings' },
      ];
    case 'mechanic':
      return [
        { id: 'fleet', icon: '🚛', label: 'Vehicles' },
        { id: 'inspect', icon: '📋', label: 'Inspect' },
        { id: 'settings', icon: '⚙️', label: 'Settings' },
      ];
    case 'broker':
      return [
        { id: 'fleet', icon: '🚛', label: 'Carriers' },
        { id: 'trips', icon: '📦', label: 'Loads' },
        { id: 'settings', icon: '⚙️', label: 'Settings' },
      ];
    default:
      return [];
  }
}

function roleLabel(r) {
  return { 'owner-operator': 'Owner Operator', carrier: 'Carrier', driver: 'Driver', mechanic: 'Mechanic', broker: 'Broker' }[r] || r;
}

window._switchTab = function(tab) {
  currentTab = tab;
  // Update tab bar active state
  document.querySelectorAll('.tab-bar button').forEach(btn => {
    btn.classList.toggle('active', btn.textContent.trim().toLowerCase().includes(tab) || btn.onclick.toString().includes(tab));
  });
  renderCurrentTab();
};

function renderCurrentTab() {
  const content = document.getElementById('page-content');
  if (!content) return;

  switch (currentTab) {
    case 'fleet':
      renderFleet(content, { role, carrierDOT, userName });
      break;
    case 'inspect':
      renderInspect(content, { role, carrierDOT, userName });
      break;
    case 'drivers':
      renderDriver(content, { role, carrierDOT, userName });
      break;
    case 'trips':
      renderTrips(content, { role, carrierDOT, userName });
      break;
    case 'settings':
      renderSettings(content, { role, carrierDOT, userName, appVersion: APP_VERSION });
      break;
    default:
      content.innerHTML = '<div class="page"><p>Unknown tab</p></div>';
  }
}

// ─── TOAST UTILITY ──────────────────────────────────────────────────

export function showToast(msg, duration = 2500) {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = msg;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), duration);
}

export { API_BASE, role, carrierDOT, userName };

// ─── BOOT ───────────────────────────────────────────────────────────
init();
